/// <reference types="google.maps" />
import { Injectable, inject } from '@angular/core';
import { ENV } from '@bodyanalytics/app-config';

export type GoogleMapsLibrary = 'places' | 'geometry' | 'marker' | 'drawing' | 'visualization';

export interface GoogleMapsLoadOptions {
  /**
   * Libraries the caller needs. `places` is ALWAYS requested on the single
   * script injection (the address autocomplete lives alongside the map), so
   * this only affects the readiness check for callers that need a specific
   * namespace to exist before they proceed.
   */
  libraries?: GoogleMapsLibrary[];
}

/**
 * Lazy loader for the Google Maps JavaScript API (shared by the calendar map
 * page and the address-autocomplete component in ui-core).
 *
 * The Maps <script> used to be a synchronous, render-blocking tag in every
 * index.html variant, loaded on every route even though only the event-details
 * page renders a map (Lighthouse flagged it on /calendar/search). This service
 * injects the script on demand, exactly once, using the browser key from the
 * environment (`mapsApiKey`) instead of a key hardcoded in HTML.
 *
 * `load()` resolves `true` when `google.maps` (and every requested library
 * namespace) is available and `false` on any failure (missing key, script
 * error) — callers keep their existing graceful degradation (the map stays
 * hidden behind `@if (mapCenter)`, the address field stays a plain input).
 */
@Injectable({ providedIn: 'root' })
export class GoogleMapsLoaderService {
  // Optional: TestBeds that never render a map don't have to provide ENV.
  private env = inject(ENV, { optional: true });
  private loadPromise: Promise<boolean> | null = null;

  load(options: GoogleMapsLoadOptions = {}): Promise<boolean> {
    const libraries = options.libraries ?? [];
    if (this.isReady(libraries)) {
      return Promise.resolve(true);
    }
    if (typeof google !== 'undefined' && google?.maps && !this.isReady(libraries)) {
      // Maps is present but a requested library is not (e.g. a legacy script
      // tag without `libraries=places`). Injecting a second bootstrap is not
      // supported by Maps JS — report unavailable so callers degrade.
      return Promise.resolve(false);
    }
    if (this.loadPromise) {
      return this.loadPromise.then(ok => ok && this.isReady(libraries));
    }

    const key = this.env?.mapsApiKey;
    if (!key) {
      return Promise.resolve(false);
    }

    this.loadPromise = new Promise<boolean>(resolve => {
      const script = document.createElement('script');
      // `libraries=places` rides along on the one bootstrap so the address
      // autocomplete never needs a second (unsupported) script injection.
      const params = new URLSearchParams({ key, libraries: 'places' });
      script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
      script.async = true;
      script.onload = () => resolve(typeof google !== 'undefined' && !!google?.maps);
      script.onerror = () => {
        // Allow a later retry (e.g. transient network failure) instead of
        // caching the failure forever.
        this.loadPromise = null;
        resolve(false);
      };
      document.head.appendChild(script);
    });

    return this.loadPromise.then(ok => ok && this.isReady(libraries));
  }

  private isReady(libraries: GoogleMapsLibrary[]): boolean {
    if (typeof google === 'undefined' || !google?.maps) {
      return false;
    }
    const maps = google.maps as unknown as Record<string, unknown>;
    return libraries.every(lib => Boolean(maps[lib]));
  }
}
